import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import MarketingNav from "@/components/app/MarketingNav";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, ArrowRight, Loader2 } from "lucide-react";

const POLL_INTERVAL_MS = 2000;
const MAX_ATTEMPTS = 30;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [status, setStatus] = useState("checking"); // checking | paid | timeout | error
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let cancelled = false;
    let attempt = 0;
    const tick = async () => {
      if (cancelled) return;
      attempt += 1;
      setAttempts(attempt);
      try {
        const { data } = await api.get(`/billing/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refresh();
          setStatus("paid");
          return;
        }
        if (data.payment_status === "expired" || data.payment_status === "failed") {
          setStatus("error");
          return;
        }
      } catch (e) {
        // ignore transient
      }
      if (attempt >= MAX_ATTEMPTS) { setStatus("timeout"); return; }
      setTimeout(tick, POLL_INTERVAL_MS);
    };
    tick();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-background">
      <MarketingNav />
      <section className="relative border-b border-border overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />
        <div className="relative max-w-3xl mx-auto px-6 lg:px-10 py-16 lg:py-24 text-center">
          {status === "checking" && (
            <div data-testid="payment-checking">
              <Loader2 className="w-12 h-12 mx-auto animate-spin" strokeWidth={1.5} />
              <h1 className="font-semibold tracking-tight mt-6 text-3xl md:text-4xl">Confirming your payment…</h1>
              <p className="text-muted-foreground mt-2 font-mono-data text-xs uppercase tracking-widest">attempt {attempts} of {MAX_ATTEMPTS}</p>
            </div>
          )}
          {status === "paid" && (
            <div data-testid="payment-success">
              <CheckCircle2 className="w-14 h-14 mx-auto text-emerald-600" strokeWidth={1.5} />
              <h1 className="font-semibold tracking-tight mt-6 text-4xl md:text-5xl">You&apos;re on Pro.</h1>
              <p className="text-muted-foreground mt-3 max-w-md mx-auto">
                Magic Mode, AI sections, content gap analysis, multi-platform social — all unlocked. Receipt sent to your email.
              </p>
              <div className="mt-8 flex justify-center gap-3 flex-wrap">
                <Button onClick={() => navigate("/app")} className="rounded-none h-11 bg-foreground hover:bg-foreground/90" data-testid="payment-success-app">
                  Open workspace <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Link to="/app/calendar">
                  <Button variant="outline" className="rounded-none h-11">Open calendar</Button>
                </Link>
              </div>
            </div>
          )}
          {status === "timeout" && (
            <div data-testid="payment-timeout">
              <h1 className="font-semibold tracking-tight text-3xl">Still processing…</h1>
              <p className="text-muted-foreground mt-3">Your payment is going through, but it&apos;s taking longer than usual. You can safely close this page — we&apos;ll email you when it&apos;s complete.</p>
              <Link to="/app" className="inline-block mt-6"><Button className="rounded-none h-11 bg-foreground hover:bg-foreground/90">Back to app</Button></Link>
            </div>
          )}
          {status === "error" && (
            <div data-testid="payment-error">
              <h1 className="font-semibold tracking-tight text-3xl">Something went wrong.</h1>
              <p className="text-muted-foreground mt-3">We couldn&apos;t confirm your payment. If your card was charged, drop us a note and we&apos;ll fix it.</p>
              <Link to="/pricing" className="inline-block mt-6"><Button variant="outline" className="rounded-none h-11">Try again</Button></Link>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
